import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ShieldCheck } from 'lucide-react';
import { isDischargeDisposition, isIntendedStayCode, type NphiesAdmissionDetailsDto, type NphiesAdmissionDetailsInput } from '@his/shared';
import { put } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { tr } from '@/lib/i18n-core';
import { Field } from '@/components/ui/primitives';
import { ChoiceButtons } from '@/components/ui/choice-buttons';
import { useToast } from '@/components/ui/toast';
import { ShellPanel } from '@/components/clinical-shell/ShellPanel';
import type { Admission } from '../types';
import { dispositionChoices, intendedStayChoices, type CodedCause } from '../stay-codes';
import { CauseOfDeathField, SpecialtySelect, useSpecialties } from '../StayCodeFields';

export function DischargeInsurerDetails({ admission }: { admission: Admission }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { can } = useAuth();
  const [changingCause, setChangingCause] = useState(false);
  const specialties = useSpecialties();
  const save = useMutation({
    mutationFn: (body: Partial<NphiesAdmissionDetailsInput>) => put<NphiesAdmissionDetailsDto>(`/integrations/nphies/admission-details/${admission.id}`, body),
    onSuccess: () => {
      toast.success(tr('ipd', 'insurer_details_saved'));
      setChangingCause(false);
      void queryClient.invalidateQueries({ queryKey: ['admission', admission.id] });
    },
    onError: (error) => toast.error(tr('ipd', 'could_not_save_the_insurer_details'), describeError(error)),
  });
  const disabled = !can('preauth.manage', 'nphies.preauth.send', 'admission.manage') || save.isPending;
  const stay = isIntendedStayCode(admission.intendedStayCode) ? admission.intendedStayCode : null;
  const disposition = isDischargeDisposition(admission.dischargeDispositionCode) ? admission.dischargeDispositionCode : null;
  const storedCause: CodedCause | null = admission.causeOfDeathCode && !changingCause ? { code: admission.causeOfDeathCode, title: '' } : null;

  return (
    <ShellPanel icon={ShieldCheck} title={tr('ipd', 'insurer_discharge_details')} description={tr('ipd', 'insurer_discharge_details_desc')}>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label={tr('ipd', 'intended_stay')} hint={tr('ipd', 'intended_stay_hint')}>
          <ChoiceButtons label={tr('ipd', 'intended_stay')} value={stay} disabled={disabled} onChange={(value) => save.mutate({ intendedStayCode: value })} options={intendedStayChoices()} />
        </Field>
        <Field label={tr('ipd', 'discharge_specialty')} hint={tr('ipd', 'discharge_specialty_hint')}>
          <SpecialtySelect
            value={admission.dischargeSpecialtyId ?? ''}
            disabled={disabled}
            onChange={(value) => save.mutate({ dischargeSpecialtyId: value || null })}
            specialties={specialties.data ?? []}
          />
        </Field>
        <Field label={tr('ipd', 'discharge_disposition')} hint={tr('ipd', 'discharge_disposition_hint')} className="sm:col-span-2">
          <ChoiceButtons
            layout="chips"
            size="sm"
            label={tr('ipd', 'discharge_disposition')}
            value={disposition}
            disabled={disabled}
            onChange={(value) => save.mutate({ dischargeDisposition: value })}
            options={dispositionChoices(admission.status)}
          />
        </Field>
        {admission.status === 'expired' && (
          <Field label={tr('ipd', 'cause_of_death_code')} hint={tr('ipd', 'cause_of_death_code_hint')} className="sm:col-span-2">
            <CauseOfDeathField
              value={storedCause}
              disabled={disabled}
              departmentId={admission.departmentId ?? null}
              onChange={(cause) => (cause ? save.mutate({ causeOfDeathCode: cause.code }) : setChangingCause(true))}
            />
          </Field>
        )}
      </div>
    </ShellPanel>
  );
}
